export type RollClassification = 'opened' | 'rolled';

/** Terminal reasons meaning a position ended on its own, NOT via an active roll. */
const NON_ROLL_TERMINAL: readonly string[] = ['expired', 'assigned', 'exercised'];

export interface ClassifyLeg {
  closeReason: string | null;
}

export interface ClassifyPosition {
  rollNumber: number;
  closeReason?: string | null;
  legs: ClassifyLeg[];
}

/**
 * Classify whether `current` is a roll of its predecessor or a fresh open.
 * Ground truth is the PREDECESSOR's terminal state — never `current.rollNumber`
 * or chain sequence. A position is a ROLL only when it has a predecessor that
 * was actively closed (not expired/assigned/exercised).
 */
export function classifyPositionEvent(
  current: ClassifyPosition,
  prior: ClassifyPosition | null,
): RollClassification {
  if (!prior || current.rollNumber === 0) return 'opened';
  if (priorEndedOnItsOwn(prior)) return 'opened';
  return 'rolled';
}

/**
 * Mirror ChainFlow precedence: leg-level terminal evidence first, position closeReason as fallback.
 * When leg evidence exists, it is authoritative — a mix of terminal (expired/assigned/exercised)
 * and actively-closed ("closed") legs means the position did NOT end purely on its own (some legs
 * were rolled), even if a coarse position-level closeReason says otherwise. The position closeReason
 * is only consulted when there is no leg evidence at all.
 */
function priorEndedOnItsOwn(prior: ClassifyPosition): boolean {
  if (prior.legs.length > 0) {
    return prior.legs.every((l) => l.closeReason != null && NON_ROLL_TERMINAL.includes(l.closeReason));
  }
  return prior.closeReason != null && NON_ROLL_TERMINAL.includes(prior.closeReason);
}
